'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { toolRegistry } from '@/lib/tool-registry';

// Dynamic imports for heavy client components
const JSONFormatterClient = dynamic(() => import('./JSONFormatter'), { ssr: false });
const PasswordGeneratorClient = dynamic(() => import('./PasswordGenerator'), { ssr: false });
const UsernameGeneratorClient = dynamic(() => import('./UsernameGenerator'), { ssr: false });
const TextUtilitiesClient = dynamic(() => import('./TextUtilities'), { ssr: false });
const TextFormatterClient = dynamic(() => import('./TextFormatter'), { ssr: false });
const GrammarCheckerClient = dynamic(() => import('./GrammarChecker'), { ssr: false });
const MergePDFClient = dynamic(() => import('./MergePDF'), { ssr: false });
const SplitPDFClient = dynamic(() => import('./SplitPDF'), { ssr: false });
const JPGToPDFClient = dynamic(() => import('./JPGToPDF'), { ssr: false });
const CompressPDFClient = dynamic(() => import('./CompressPDF'), { ssr: false });
const QRCodeGeneratorClient = dynamic(() => import('./QRCodeGenerator'), { ssr: false });
const JWTDebuggerClient = dynamic(() => import('./JWTDebugger'), { ssr: false });
const BcryptHasherClient = dynamic(() => import('./BcryptHasher'), { ssr: false });
const SQLInjectionCheckerClient = dynamic(() => import('./SQLInjectionChecker'), { ssr: false });
const FinancialCalculatorsClient = dynamic(() => import('./FinancialCalculators'), { ssr: false });
const InvoiceGeneratorClient = dynamic(() => import('./InvoiceGenerator'), { ssr: false });
const ExpenseTrackerClient = dynamic(() => import('./ExpenseTracker'), { ssr: false });
const BusinessGeneratorsClient = dynamic(() => import('./BusinessGenerators'), { ssr: false });
const AIPromptGeneratorClient = dynamic(() => import('./AIPromptGenerator'), { ssr: false });
const IFSCCheckerClient = dynamic(() => import('./IFSCChecker'), { ssr: false });
const PincodeFinderClient = dynamic(() => import('./PincodeFinder'), { ssr: false });
const MutualFundCalculatorClient = dynamic(() => import('./MutualFundCalculator'), { ssr: false });
const FuelCostCalculatorClient = dynamic(() => import('./FuelCostCalculator'), { ssr: false });
const GenericToolClient = dynamic(() => import('./GenericTool'), { ssr: false });

// New Specialized Components
const MathSolverClient = dynamic(() => import('./MathSolver'), { ssr: false });
const TextToQuizClient = dynamic(() => import('./TextToQuiz'), { ssr: false });
const URLSlugGeneratorClient = dynamic(() => import('./URLSlugGenerator'), { ssr: false });
const BrandNameGeneratorClient = dynamic(() => import('./BrandNameGenerator'), { ssr: false });
const AINewsletterWriterClient = dynamic(() => import('./AINewsletterWriter'), { ssr: false });
const MissionVisionGeneratorClient = dynamic(() => import('./MissionVisionGenerator'), { ssr: false });
const PressReleaseGeneratorClient = dynamic(() => import('./PressReleaseGenerator'), { ssr: false });
const CustomerSurveyGeneratorClient = dynamic(() => import('./CustomerSurveyGenerator'), { ssr: false });
const TaglineGeneratorClient = dynamic(() => import('./TaglineGenerator'), { ssr: false });
const AIContentDetectorClient = dynamic(() => import('./AIContentDetector'), { ssr: false });
const AITextHumanizerClient = dynamic(() => import('./AITextHumanizer'), { ssr: false });
const AIImageGeneratorClient = dynamic(() => import('./AIImageGenerator'), { ssr: false });

interface ToolRendererProps {
    slug: string;
}

export default function ToolRenderer({ slug }: ToolRendererProps) {
    const toolData = toolRegistry[slug];
    if (!toolData) return null;
    const { config } = toolData;

    switch (config.componentType) {
        case 'json': return <JSONFormatterClient key={slug} />;
        case 'password': return <PasswordGeneratorClient key={slug} />;
        case 'username': return <UsernameGeneratorClient key={slug} />;
        case 'text-utilities': return <TextUtilitiesClient key={slug} standalone={true} {...config.componentProps} />;
        case 'text-formatter': return <TextFormatterClient key={slug} />;
        case 'grammar': return <GrammarCheckerClient key={slug} />;
        case 'pdf-merge': return <MergePDFClient key={slug} />;
        case 'pdf-split': return <SplitPDFClient key={slug} />;
        case 'jpg-to-pdf': return <JPGToPDFClient key={slug} />;
        case 'pdf-compress': return <CompressPDFClient key={slug} />;
        case 'qr-generator': return <QRCodeGeneratorClient key={slug} />;
        case 'jwt-debugger': return <JWTDebuggerClient key={slug} />;
        case 'bcrypt': return <BcryptHasherClient key={slug} />;
        case 'sql-checker': return <SQLInjectionCheckerClient key={slug} />;
        case 'financial-calculator': return <FinancialCalculatorsClient key={slug} defaultTool={(config.componentProps && config.componentProps.defaultTool) || 'gst'} />;
        case 'invoice': return <InvoiceGeneratorClient key={slug} />;
        case 'expense-tracker': return <ExpenseTrackerClient key={slug} />;
        case 'business-generator': return <BusinessGeneratorsClient key={slug} type={(config.componentProps && config.componentProps.type) || 'invoice'} />;
        case 'ai-prompt-generator': return <AIPromptGeneratorClient key={slug} />;
        case 'ifsc-checker': return <IFSCCheckerClient key={slug} />;
        case 'pincode-finder': return <PincodeFinderClient key={slug} />;
        case 'mutual-fund-calc': return <MutualFundCalculatorClient key={slug} />;
        case 'fuel-calc': return <FuelCostCalculatorClient key={slug} />;
        
        // Specialized Mappings
        case 'math-solver': return <MathSolverClient key={slug} />;
        case 'text-to-quiz': return <TextToQuizClient key={slug} />;
        case 'url-slug': return <URLSlugGeneratorClient key={slug} />;
        case 'brand-name': return <BrandNameGeneratorClient key={slug} />;
        case 'newsletter': return <AINewsletterWriterClient key={slug} />;
        case 'mission-vision': return <MissionVisionGeneratorClient key={slug} />;
        case 'press-release': return <PressReleaseGeneratorClient key={slug} />;
        case 'survey': return <CustomerSurveyGeneratorClient key={slug} />;
        case 'tagline': return <TaglineGeneratorClient key={slug} />;
        case 'ai-detector': return <AIContentDetectorClient key={slug} />;
        case 'ai-humanizer': return <AITextHumanizerClient key={slug} />;
        case 'ai-image-generator': return <AIImageGeneratorClient key={slug} />;

        case 'generic':
        default:
            return <GenericToolClient key={slug} tool={config} />;
    }
}
